"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import dynamic from "next/dynamic";
import emitter from "@packages/lib/eventEmitter/eventEmitter";
import { useRouter, useSearchParams } from "next/navigation";
import {
  GA4DataLayer,
} from "@packages/lib/utlils/helper-function";
import { GAConstants } from "@packages/shared-components/services/constants";
import Image from "next/image";
import OpenDaysTypeTabs from "@packages/shared-components/sr-page/opendays-type-tabs/opendays-type-tabs";
import { useOpenDaysFilterContext } from "@packages/lib/utlils/use-context/opendays-filter-context";

const DownarrowSvg = dynamic(() => import("./downarrow-svg"), { ssr: false });

// SR-page facet anchor style: keeps the option looking unchanged while the URL
// shows on hover (status bar) and is crawlable. Matches inner-link-formation.tsx.
const facetLinkStyle = {
  color: "inherit",
  textDecoration: "none",
  display: "contents",
} as const;

interface OpenDayUniversity {
  college_id: number;
  collegeName: string;
  collegeTextKey: string;
  collegeNameDisplay: string;
  openDayCount: number;
}

interface SearchFilterButtonsOpendaysProps {
  commonGAAttrs?: any;
  universitiesList?: OpenDayUniversity[];
}

const SearchFilterButtonsOpendays = ({
  commonGAAttrs,
  universitiesList = [],
}: SearchFilterButtonsOpendaysProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Read filter state from shared context
  const { filters, appliedFilterCount, setFilters, setIsLoading } =
    useOpenDaysFilterContext();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [filteredList, setFilteredList] = useState<OpenDayUniversity[]>(universitiesList);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Build the open days URL for a university option (postgraduate uses level=l).
  const buildUniversityUrl = useCallback(
    (item: OpenDayUniversity) => {
      const isPostgraduate = searchParams?.get("level") === "l";
      return isPostgraduate
        ? `/open-days/${item.collegeTextKey}/postgraduate/${item.college_id}/`
        : `/open-days/${item.collegeTextKey}/${item.college_id}/`;
    },
    [searchParams],
  );

  useEffect(() => {
    const bodyContainer: any = document.getElementById("bodyContainer");
    const footer: any = document.getElementById("footerComponent");
    if (!bodyContainer || !footer) return;
    emitter.emit("searchpageloading", false);
    bodyContainer?.classList?.remove("hidden");
    footer?.classList?.remove("hidden");
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        inputRef.current &&
        !inputRef.current.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, {
      passive: true,
    });
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  const onSearchBoxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setShowError(false);
      const value = e.target.value;
      setSearchValue(value);
      setIsDropdownOpen(true);

      if (value.trim().length === 0) {
        setFilteredList(universitiesList);
        return;
      }

      const searchTerms = value
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      const filtered = universitiesList.filter((item) => {
        const name = item.collegeNameDisplay.toLowerCase();
        return searchTerms.some((term) => name.includes(term));
      });

      setFilteredList(filtered);
    },
    [universitiesList],
  );

  const onSearchBoxClick = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      setIsDropdownOpen(true);
      const target = e.target as HTMLInputElement;
      const value = (target.value || "").toLowerCase();

      if (value.trim().length === 0) {
        setFilteredList(universitiesList);
        return;
      }

      const filtered = universitiesList.filter((item) =>
        item.collegeNameDisplay.toLowerCase().includes(value.trim()),
      );
      setFilteredList(filtered);
    },
    [universitiesList],
  );

  const handleUniversityClick = useCallback(
    (item: OpenDayUniversity) => {
      setSearchValue(item.collegeNameDisplay);
      setIsDropdownOpen(false);
      setShowError(false);
      setIsLoading(true);

      // Navigate to university-specific open days page
      // URL format: /open-days/{university-text-key}/postgraduate/{college-id}/ (for postgraduate)
      // URL format: /open-days/{university-text-key}/{college-id}/ (for undergraduate or no level)
      router.push(buildUniversityUrl(item));
    },
    [router, setIsLoading, buildUniversityUrl],
  );

  const handleSearchClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!searchValue.trim()) {
        setShowError(true);
        setIsDropdownOpen(true);
        return;
      }
      setShowError(false);
    },
    [searchValue],
  );

  const filterEvents = useCallback(
    (eventName: string | null | undefined) => {
      GA4DataLayer({
        event: GAConstants.track_event,
        event_name: "filter_panel_interaction",
        ...commonGAAttrs,
        click_text: eventName,
      });
      emitter.emit("isfilterOpen", eventName);
      emitter.emit("pauseVideo");
    },
    [commonGAAttrs],
  );

  const resetFilter = useCallback(() => {
    GA4DataLayer({
      event: GAConstants.track_event,
      event_name: "reset_filter",
      click_text: "reset_all_filters",
      page_name: localStorage?.getItem("gaPageName") || "opendays_landing",
      ...commonGAAttrs,
    });

    setSearchValue("");
    setFilteredList(universitiesList);
    setShowError(false);

    const cleared = {
      studyLevel: "",
      provider: "",
      providerId: "",
      location: "",
      distance: "",
      postcode: "",
      monthYear: "",
      eventType: "",
      pageno: "1",
      dateFrom: "",
      dateTo: "",
      isVirtualTour: filters.isVirtualTour || false,
    };

    setFilters(cleared);
    setIsLoading(true);
    router.push(
      filters.isVirtualTour
        ? "/open-days/search/?date=virtual_tour"
        : "/open-days/",
    );
  }, [universitiesList, filters.isVirtualTour, setFilters, setIsLoading, router, commonGAAttrs]);

  return (
    <>
      <OpenDaysTypeTabs commonGAAttrs={commonGAAttrs} />
      <section className="bg-grey-600 px-[12px] py-[16px] fixed bottom-0 w-full lg:sticky lg:top-0 z-[4] min-h-[72px] lg:min-h-[64px]">
        <div className="max-w-container mx-auto flex gap-[8px] small">
          {/* University Search Input */}
          <div className="hidden lg:flex h-[44px] w-[280px] px-[4px] items-center bg-white rounded-[32px] border border-neutral300 hover:border-primary-500 shadow-custom-1 gap-x-[10px]">
            <div className="flex relative w-full">
              <input
                ref={inputRef}
                value={searchValue}
                onChange={onSearchBoxChange}
                onClick={onSearchBoxClick}
                type="text"
                className="form-control w-full focus:outline-none pl-[24px] bg-transparent small text-black placeholder:text-gray-500 border-none"
                aria-label="Search universities with open days"
                placeholder="Search unis"
              />
              {isDropdownOpen && (
                <div
                  ref={dropdownRef}
                  className="w-[343px] z-[5] flex flex-col absolute bg-white shadow-custom-3 rounded-[8px] left-[0] top-[40px] overflow-hidden"
                >
                  <div className="px-[16px] py-[12px] cursor-pointer">
                    {showError && (
                      <p className="text-red-500 mt-2 text-sm">
                        Please select a university
                      </p>
                    )}
                  </div>
                  <div className="flex px-[16px] py-[8px]">
                    <ul className="flex flex-wrap gap-[8px]">
                      <li className="flex text-nowrap select-none rounded-[4px] font-bold uppercase px-[8px] bg-grey-100 text-grey-500 xs-small">
                        University
                      </li>
                    </ul>
                  </div>
                  <ul className="custom-scrollbar-2 max-h-[205px] overflow-y-scroll mr-[4px]">
                    {filteredList.length > 0 ? (
                      filteredList.map((item) => {
                        const url = buildUniversityUrl(item);
                        return (
                          <li
                            key={item.collegeTextKey}
                            className="hover:bg-blue-50 px-[16px] py-[10px] small cursor-pointer"
                          >
                            <a
                              href={url}
                              style={facetLinkStyle}
                              onClick={(e) => {
                                e.preventDefault();
                                handleUniversityClick(item);
                              }}
                            >
                              <span className="hover:underline text-black">
                                {item.collegeNameDisplay}
                                <span className="text-grey500 ml-2">
                                  {item.openDayCount} Open{" "}
                                  {item.openDayCount === 1 ? "Day" : "Days"}
                                </span>
                              </span>
                            </a>
                          </li>
                        );
                      })
                    ) : (
                      <li className="px-[16px] py-[10px] text-sm text-grey-500">
                        No universities found
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            <button
              type="submit"
              onClick={handleSearchClick}
              className="btn btn-primary h-[36px] min-w-[36px] p-[8px]"
            >
              <Image
                src="/static/assets/icons/search_icon.svg"
                width="18"
                height="18"
                alt="Search icon"
              />
            </button>
          </div>

          {/* Filter Button */}
          <button
            type="button"
            onClick={() => filterEvents("all")}
            className="flex items-center justify-center gap-[8px] btn grow w-fit px-[12px] md:px-[16px] bg-primary-100 hover:bg-primary-200 text-grey300 lg:grow-0 lg:shrink-0"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 3.90625V1.875M10 3.90625C8.87817 3.90625 7.96875 4.81567 7.96875 5.9375C7.96875 7.05933 8.87817 7.96875 10 7.96875M10 3.90625C11.1218 3.90625 12.0312 4.81567 12.0312 5.9375C12.0312 7.05933 11.1218 7.96875 10 7.96875M3.90625 16.0938C5.02808 16.0938 5.9375 15.1843 5.9375 14.0625C5.9375 12.9407 5.02808 12.0312 3.90625 12.0312M3.90625 16.0938C2.78442 16.0938 1.875 15.1843 1.875 14.0625C1.875 12.9407 2.78442 12.0312 3.90625 12.0312M3.90625 16.0938V18.125M3.90625 12.0312V1.875M10 7.96875V18.125M16.0938 16.0938C17.2156 16.0938 18.125 15.1843 18.125 14.0625C18.125 12.9407 17.2156 12.0312 16.0938 12.0312M16.0938 16.0938C14.9719 16.0938 14.0625 15.1843 14.0625 14.0625C14.0625 12.9407 14.9719 12.0312 16.0938 12.0312M16.0938 16.0938V18.125M16.0938 12.0312V1.875"
                stroke="#333333"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Filter ({appliedFilterCount})
          </button>

          {/* Desktop Filter Buttons: Year, University, Location */}
          <div className="hidden lg:flex items-center justify-center gap-[8px] lg:shrink-0">
            {/* Hide Year button when virtual tour is active */}
            {!filters.isVirtualTour && (
              <button
                type="button"
                className="flex items-center gap-[8px] btn w-fit px-[12px] md:px-[16px] bg-grey-100 hover:bg-grey-200 text-grey300"
                onClick={() => filterEvents("open-day-type")}
              >
                Open day type
                <DownarrowSvg />
              </button>
            )}
            <button
              type="button"
              className="flex items-center gap-[8px] btn w-fit px-[12px] md:px-[16px] bg-grey-100 hover:bg-grey-200 text-grey300"
              onClick={() => filterEvents("location")}
            >
              Region
              <DownarrowSvg />
            </button>
            <button
              type="button"
              className="flex items-center gap-[8px] btn w-fit px-[12px] md:px-[16px] bg-grey-100 hover:bg-grey-200 text-grey300"
              onClick={() => filterEvents("study-level")}
            >
              Study level
              <DownarrowSvg />
            </button>
          </div>

          {/* Reset Button - only shown when filters are active */}
          {appliedFilterCount > 0 && (
            <button
              type="button"
              className="flex items-center justify-center gap-[4px] cursor-pointer px-0 text-grey-50 hover:underline xl:px-[16px] lg:shrink-0"
              onClick={resetFilter}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M1.8173 1.8173C2.24037 1.39423 2.9263 1.39423 3.34937 1.8173L8 6.46794L12.6506 1.8173C13.0737 1.39423 13.7596 1.39423 14.1827 1.8173C14.6058 2.24037 14.6058 2.9263 14.1827 3.34937L9.53206 8L14.1827 12.6506C14.6058 13.0737 14.6058 13.7596 14.1827 14.1827C13.7596 14.6058 13.0737 14.6058 12.6506 14.1827L8 9.53206L3.34937 14.1827C2.9263 14.6058 2.24037 14.6058 1.8173 14.1827C1.39423 13.7596 1.39423 13.0737 1.8173 12.6506L6.46794 8L1.8173 3.34937C1.39423 2.9263 1.39423 2.24037 1.8173 1.8173Z"
                  fill="white"
                />
              </svg>
              Reset
            </button>
          )}
        </div>
      </section>
    </>
  );
};

export default SearchFilterButtonsOpendays;
